import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaTrophy, FaChartLine, FaGift, FaHome, FaQuestionCircle, FaEnvelope, FaUsers } from 'react-icons/fa';

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { path: '/', label: 'Accueil', icon: FaHome },
    { path: '/reviews', label: 'Avis', icon: FaTrophy },
    { path: '/comparisons', label: 'Comparaisons', icon: FaChartLine },
    { path: '/bonuses', label: 'Bonus', icon: FaGift },
  ];

  return (
    <header
      className={`fixed top-[30px] max-md:top-[110px] left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'glass-strong border-b border-white/10 shadow-2xl' 
          : 'bg-transparent'
      }`}
    >
      <nav className="container mx-auto px-4 py-5">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="relative">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center transform group-hover:rotate-12 group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-purple-500/50">
                <img 
                  src="/images/logo.webp" 
                  alt="logo" 
                  className="w-12 h-12 object-contain"
                  width="48"
                  height="48"
                  loading="eager"
                  decoding="async"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 via-purple-500 to-pink-500 rounded-2xl blur-lg opacity-50 group-hover:opacity-75 transition-opacity -z-10"></div>
            </div>
            <span className="text-2xl font-bold heading-font text-gradient">Parismin</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative flex items-center space-x-2 px-5 py-2.5 rounded-xl transition-all duration-300 ${
                    isActive
                      ? 'bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 text-white shadow-lg neon-glow'
                      : 'text-gray-300 hover:text-white glass hover:scale-105'
                  }`}
                >
                  <Icon className="text-sm" />
                  <span className="font-semibold text-sm">{item.label}</span>
                  {isActive && (
                    <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-white rounded-full"></div>
                  )}
                </Link>
              );
            })}
            <div className="ml-2 flex items-center space-x-2">
              <Link
                to="/about"
                className={`relative flex items-center space-x-2 px-4 py-2.5 rounded-xl transition-all duration-300 ${
                  location.pathname === '/about'
                    ? 'bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 text-white shadow-lg neon-glow'
                    : 'text-gray-300 hover:text-white glass hover:scale-105'
                }`}
              >
                <FaUsers className="text-sm" />
              </Link>
              <Link
                to="/faq"
                className={`relative flex items-center space-x-2 px-4 py-2.5 rounded-xl transition-all duration-300 ${
                  location.pathname === '/faq'
                    ? 'bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 text-white shadow-lg neon-glow'
                    : 'text-gray-300 hover:text-white glass hover:scale-105'
                }`}
              >
                <FaQuestionCircle className="text-sm" />
              </Link>
              <Link
                to="/contact"
                className={`relative flex items-center space-x-2 px-4 py-2.5 rounded-xl transition-all duration-300 ${
                  location.pathname === '/contact'
                    ? 'bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 text-white shadow-lg neon-glow'
                    : 'text-gray-300 hover:text-white glass hover:scale-105'
                }`}
              >
                <FaEnvelope className="text-sm" />
              </Link>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-white p-3 rounded-xl glass hover:bg-white/10 transition-all"
          >
            {isMobileMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 space-y-2 animate-in slide-in-from-top">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center space-x-3 px-5 py-3.5 rounded-xl transition-all ${
                    isActive
                      ? 'bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 text-white shadow-lg'
                      : 'text-gray-300 glass hover:bg-white/10'
                  }`}
                >
                  <Icon />
                  <span className="font-semibold">{item.label}</span>
                </Link>
              );
            })}
            <Link
              to="/about"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`flex items-center space-x-3 px-5 py-3.5 rounded-xl transition-all ${
                location.pathname === '/about'
                  ? 'bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 text-white shadow-lg'
                  : 'text-gray-300 glass hover:bg-white/10'
              }`}
            >
              <FaUsers />
              <span className="font-semibold">À Propos</span>
            </Link>
            <Link
              to="/faq"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`flex items-center space-x-3 px-5 py-3.5 rounded-xl transition-all ${
                location.pathname === '/faq'
                  ? 'bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 text-white shadow-lg'
                  : 'text-gray-300 glass hover:bg-white/10'
              }`}
            >
              <FaQuestionCircle />
              <span className="font-semibold">FAQ</span>
            </Link>
            <Link
              to="/contact"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`flex items-center space-x-3 px-5 py-3.5 rounded-xl transition-all ${
                location.pathname === '/contact'
                  ? 'bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 text-white shadow-lg'
                  : 'text-gray-300 glass hover:bg-white/10'
              }`}
            >
              <FaEnvelope />
              <span className="font-semibold">Contact</span>
            </Link>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;

